import { CourseRequirementType, EPassable, ICourse, ICourseRequirement, ProgressItem, RequirementKey, UserCourseCompletionState, UserCourseCompletionStats } from "types/courses";
import { BrandliveEvent } from "types/working-model";

export const checkPassability = (event: BrandliveEvent, course: ICourse, progress: Record<number, ProgressItem>) => {
	const requirements = course.requirements;
	if (!requirements) {
		return EPassable.undetermined;
	}

	let passable = EPassable.yes;
	const sessionsMap = new Map(event.sessions.map(session => [session.session, session]));
	for (const requirement of requirements) {
		const session = sessionsMap.get(requirement.session_id);

		// course requires a non-existant session (maybe user doesn't have access to it)
		// so the course cannot be passed
		if (!session) {
			passable = EPassable.no;
			break;
		}

		const sessionProg = progress[requirement.session_id];

		switch (requirement.type) {
			case CourseRequirementType.pulse_check: {
				console.warn('pulse checks no longer count');
				break;
			}

			case CourseRequirementType.quizzes_passed: { 
				// TODO: track number of quizzes passed vs number of quizzes that CAN be passed 
				// because the user may have failed so many that passage is impossible
				break;
			}
		}
	}

	return passable;
};

export const userStatsToPassState = (course: ICourse, stats: UserCourseCompletionStats) => {
	if (!course.requirements) {
		return {
			passState: UserCourseCompletionState.Wait,
			passDate: undefined,
			progress: {}
		};
	}

	const requirements = course.requirements;
	const progress: Record<number, ProgressItem> = {};
	let hasStats = false;

	for (const requirement of requirements) {
		if (!progress[requirement.session_id]) {
			progress[requirement.session_id] = {
				watched_seconds: 0,
				pulse_checks_acknowledged: 0,
				quizzes_passed: 0,
				poll_submissions: 0
			};
		}

		const key: RequirementKey = `req-${requirement.id}`;
		const stat = stats[key];
		if (stat) {
			hasStats = true;
		}

		const statAmount = Number(stats[key] || 0);

		switch (requirement.type) {
			case CourseRequirementType.pulse_check: {
				progress[requirement.session_id].pulse_checks_acknowledged = statAmount;
				break;
			}

			case CourseRequirementType.watched_minute: {
				progress[requirement.session_id].watched_seconds = statAmount;
				break;
			}

			case CourseRequirementType.poll_submissions: { 
				progress[requirement.session_id].poll_submissions = statAmount;
				break;
			}

			case CourseRequirementType.quizzes_passed: { 
				progress[requirement.session_id].quizzes_passed = statAmount;
				break;
			}
		}
	}

	return {
		passState: stats.state,
		passDate: stats.date,
		progress
	};
};

export const estimatePassState = (course: ICourse, progress: Record<number, ProgressItem>) => {
	if (!course.requirements) {
		return false;
	}

	const requirements = course.requirements;
	const total = requirements.length;
	let completed = 0;

	for (const requirement of requirements) {
		const prog = progress[requirement.session_id];
		if (!prog) {
			continue;
		}

		switch (requirement.type) {
			case CourseRequirementType.pulse_check: {
				if (prog.pulse_checks_acknowledged >= requirement.required_amount - 1) {
					completed++;
				}
				break;
			}

			case CourseRequirementType.watched_minute: {
				if (prog.watched_seconds / 60 >= requirement.required_amount - 1) {
					completed++;
				}
				break;
			}
			case CourseRequirementType.poll_submissions: {
				if (prog.poll_submissions >= requirement.required_amount - 1) {
					completed++;
				}
				break;
			}
			case CourseRequirementType.quizzes_passed: {
				if (prog.quizzes_passed >= requirement.required_amount - 1) {
					completed++;
				}
				break;
			}
		}
	}

	return completed >= total;
};

export const checkIfRequirementPassed = (
	requirement: ICourseRequirement,
	completion: Record<number, ProgressItem> | undefined,
): boolean => {
	const reqType = requirement.type;
	const passThreshold = requirement.required_amount;
	const completionData = completion?.[requirement.session_id];

	if (reqType === CourseRequirementType.watched_minute) {
		return (completionData?.watched_seconds || 0) / 60 >= passThreshold;
	}
	if (reqType === CourseRequirementType.pulse_check) {
		return (completionData?.pulse_checks_acknowledged || 0) >= passThreshold;
	}
	if (reqType === CourseRequirementType.poll_submissions) {
		return (completionData?.poll_submissions || 0) >= passThreshold;
	}
	if (reqType === CourseRequirementType.quizzes_passed) {
		return (completionData?.quizzes_passed || 0) >= passThreshold;
	}

	return false;
};
